import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CampaignIcon from "@mui/icons-material/Campaign";
import { getBroadcastStatusDisplay } from "../../utils/broadcastStatus";

const COLUMN_COUNT = 8;

// A broadcast that's actively sending can't be deleted — matches the backend's
// deleteCampaign guard (bulk.campaign.js).
const UNDELETABLE_STATUSES = new Set(["IN_PROGRESS"]);

// Matches the backend's cancelCampaign guard (bulk.campaign.js) — anything
// not already finished or cancelled can be cancelled.
const CANCELLABLE_STATUSES = new Set(["PENDING", "SCHEDULED", "IN_PROGRESS"]);

const formatBroadcastTime = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const BroadcastTable = ({
  campaigns,
  loading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onResend,
  onCancel,
  onDelete,
  onOpenBroadcast,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCampaign, setMenuCampaign] = useState(null);
  const [deleteCampaign, setDeleteCampaign] = useState(null);

  const handleMenuOpen = (event, campaign) => {
    setAnchorEl(event.currentTarget);
    setMenuCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCampaign(null);
  };

  const handleResendClick = () => {
    if (menuCampaign) onResend(menuCampaign._id);
    handleMenuClose();
  };

  const handleCancelClick = () => {
    if (menuCampaign) onCancel(menuCampaign._id);
    handleMenuClose();
  };

  const handleEditClick = () => {
    if (menuCampaign) onOpenBroadcast(menuCampaign);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (menuCampaign) setDeleteCampaign(menuCampaign);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (deleteCampaign) onDelete(deleteCampaign._id);
    setDeleteCampaign(null);
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Broadcast Time</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Labels</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Channel</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Dataset</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">Recipients</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  {[...Array(COLUMN_COUNT)].map((__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" width={cellIndex === 0 ? 70 : 90} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ padding: 4 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    <CampaignIcon sx={{ fontSize: 40, opacity: 0.5, color: (theme) => theme.palette.text.secondary }} />
                    <Typography variant="body2" color="text.secondary">
                      No broadcasts found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => {
                const statusDisplay = getBroadcastStatusDisplay(campaign.status);
                const datasetName =
                  campaign.contactGroupId && typeof campaign.contactGroupId === "object"
                    ? campaign.contactGroupId.name
                    : "[Deleted]";
                // For a scheduled broadcast, "Broadcast Time" is when it's due
                // to send, not when it was created — falls back to createdAt
                // for every other status (and if scheduledAt is somehow missing).
                const broadcastTime =
                  campaign.status === "SCHEDULED" && campaign.scheduledAt
                    ? campaign.scheduledAt
                    : campaign.createdAt;
                return (
                  <TableRow
                    key={campaign._id}
                    hover
                    onClick={() => onOpenBroadcast(campaign)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Chip
                        label={statusDisplay.label}
                        size="small"
                        color={statusDisplay.paletteKey}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatBroadcastTime(broadcastTime)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        sx={{
                          color: (theme) => theme.palette.primary.main,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {campaign.campaignName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {campaign.label ? (
                        <Chip label={campaign.label} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <WhatsAppIcon sx={{ fontSize: 16, color: "#25D366" }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          WhatsApp Business Platform
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{datasetName}</Typography>
                    </TableCell>
                    <TableCell align="right">{campaign.totalContacts ?? campaign.progress?.total ?? 0}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, campaign);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ flexShrink: 0, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick} disabled={!menuCampaign}>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleResendClick}
          disabled={!menuCampaign || (menuCampaign.progress?.failed ?? 0) === 0}
        >
          <ListItemText>Resend Failed Messages</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleCancelClick}
          disabled={!menuCampaign || !CANCELLABLE_STATUSES.has(menuCampaign.status)}
          sx={{ color: "error.main" }}
        >
          <ListItemText>Cancel Broadcast</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleDeleteClick}
          disabled={!menuCampaign || UNDELETABLE_STATUSES.has(menuCampaign.status)}
          sx={{ color: "error.main" }}
        >
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(deleteCampaign)} onClose={() => setDeleteCampaign(null)}>
        <DialogTitle>Delete Broadcast?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{deleteCampaign?.campaignName}"? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCampaign(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BroadcastTable;
